/**
 * Operaciones del carro (CHK-01).
 *
 * Funciones puras sobre la lista de lineas: ninguna toca React, el navegador ni
 * la API. Estan separadas del contexto a proposito —el contexto administra el
 * estado, `almacen-carro.ts` lo persiste y `paginas/Carro.tsx` lo dibuja— para
 * que las reglas del carro vivan en un solo archivo y se puedan leer de corrido.
 *
 * Todas devuelven un carro nuevo; ninguna modifica el que recibe.
 */
import type { Carro, ItemCarro, ProductoResumen } from '@bandu/compartido';

/**
 * Tope de unidades por linea.
 *
 * No es el stock: es un freno a un dedo pegado en el "+" y a una cantidad
 * escrita a mano. La disponibilidad de verdad se comprueba en el servidor al
 * momento de cobrar (CHK-05), nunca aca.
 */
export const MAXIMO_POR_LINEA = 99;

export const CARRO_VACIO: Carro = { items: [], subtotal: 0 };

function subtotalDe(items: ItemCarro[]): number {
  return items.reduce((suma, item) => suma + item.producto.precio * item.cantidad, 0);
}

/** Unico constructor de un carro: el subtotal nunca se pasa a mano. */
function carroCon(items: ItemCarro[]): Carro {
  return { items, subtotal: subtotalDe(items) };
}

function acotar(cantidad: number): number {
  return Math.min(Math.max(Math.trunc(cantidad), 1), MAXIMO_POR_LINEA);
}

/** Unidades totales, que es lo que muestra el globo de la cabecera. */
export function unidadesEn(carro: Carro): number {
  return carro.items.reduce((suma, item) => suma + item.cantidad, 0);
}

export function cantidadEnCarro(carro: Carro, sku: string): number {
  return carro.items.find((item) => item.producto.sku === sku)?.cantidad ?? 0;
}

/**
 * Agrega unidades de un producto.
 *
 * Si ya estaba en el carro suma sobre lo que habia —es lo que espera quien
 * vuelve a la ficha y agrega dos mas— y actualiza el resumen guardado con el
 * que acaba de ver en pantalla.
 */
export function agregar(carro: Carro, producto: ProductoResumen, cantidad = 1): Carro {
  // Agregar nunca resta: con una cantidad negativa, sumar sobre lo que habia
  // bajaba la linea y el boton igual decia "Agregado".
  if (Math.trunc(cantidad) < 1) return carro;

  const existente = carro.items.find((item) => item.producto.sku === producto.sku);

  if (existente === undefined) {
    return carroCon([...carro.items, { producto, cantidad: acotar(cantidad) }]);
  }

  return carroCon(
    carro.items.map((item) =>
      item.producto.sku === producto.sku
        ? { producto, cantidad: acotar(item.cantidad + cantidad) }
        : item,
    ),
  );
}

/** Fija la cantidad de una linea. Bajar de 1 es quitarla. */
export function cambiarCantidad(carro: Carro, sku: string, cantidad: number): Carro {
  if (cantidad < 1) return quitar(carro, sku);

  return carroCon(
    carro.items.map((item) =>
      item.producto.sku === sku ? { ...item, cantidad: acotar(cantidad) } : item,
    ),
  );
}

export function quitar(carro: Carro, sku: string): Carro {
  return carroCon(carro.items.filter((item) => item.producto.sku !== sku));
}

/**
 * Pone al dia el carro guardado contra el catalogo vigente (CHK-02).
 *
 * Lo que no viene en `vigentes` se cayo del catalogo —se oculto, se elimino— y
 * se descarta sin avisar: ofrecerle al comprador un producto que ya no existe
 * es peor que la lista mas corta. Lo que si viene se reemplaza entero, asi el
 * precio, el stock y la foto que se muestran son los de hoy.
 *
 * Conserva el orden en que se fueron agregando las lineas.
 */
export function conciliar(carro: Carro, vigentes: ProductoResumen[]): Carro {
  const porSku = new Map(vigentes.map((producto) => [producto.sku, producto]));

  const items = carro.items.flatMap<ItemCarro>((item) => {
    const vigente = porSku.get(item.producto.sku);
    return vigente === undefined ? [] : [{ producto: vigente, cantidad: item.cantidad }];
  });

  return carroCon(items);
}
